// Pick'em's reads and its one write (scenario I6,
// PLAN-2026-10-18-families-and-newcomers §9.1).
//
// A REAL LEAGUE'S ALONE for now: the practice league's simulator plays no
// pick'em yet (the screen marks the seam), so for it the read is not asked and
// the screen says so.

import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { PickemView, type PickemViewShape, type Snapshot } from '@fsp/domain';
import { messageOf, send } from './api';
import { useLeagueSource, type LeagueSource } from './leagueSource';
import { useRead } from './read';
import { leagueIdFromPath } from './route';

export function usePickem(
  period: number | undefined,
  refresh = 0,
): {
  source: LeagueSource;
  leagueId: string | null;
  snapshot: Snapshot<PickemViewShape | null>;
  reload: () => void;
} {
  const { pathname } = useLocation();
  const leagueId = useMemo(() => leagueIdFromPath(pathname), [pathname]);
  const source = useLeagueSource();
  const path =
    source === 'api' && leagueId !== null
      ? `/leagues/${leagueId}/pickem${period === undefined ? '' : `?period=${period}`}`
      : null;
  const read = useRead(path, PickemView, { refresh });
  return { source, leagueId, snapshot: read.snapshot, reload: read.reload };
}

/**
 * My team's guess at its own score for a week (Bullseye,
 * PLAN-2026-10-30-bullseye), or null to clear it; the answer is the week as it
 * now stands.
 */
export async function saveGuess(
  leagueId: string,
  period: number,
  guess: number | null,
): Promise<{ ok: true; view: PickemViewShape } | { ok: false; message: string }> {
  const reply = await send('PUT', `/leagues/${leagueId}/pickem/${period}`, { guess }, PickemView);
  if (reply.status === 'ok') return { ok: true, view: reply.data };
  return {
    ok: false,
    message: messageOf(
      reply,
      'That guess did not save. Try again.',
      'Could not reach the site. Check your connection.',
    ),
  };
}

/** My team's picks for a week, all at once; the answer is the week as it now stands. */
export async function savePicks(
  leagueId: string,
  period: number,
  picks: ReadonlyArray<{ matchupId: string; teamId: string }>,
): Promise<{ ok: true; view: PickemViewShape } | { ok: false; message: string }> {
  const reply = await send('PUT', `/leagues/${leagueId}/pickem/${period}`, { picks }, PickemView);
  if (reply.status === 'ok') return { ok: true, view: reply.data };
  return {
    ok: false,
    message: messageOf(
      reply,
      'That pick did not save. Try again.',
      'Could not reach the site. Check your connection.',
    ),
  };
}
